import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  getUser,
  updateTeacherProfile,
  updateUser,
  TeacherProfile,
  Avatar,
} from '../../services/userService';
import { useAuth } from '../../hooks/useAuth';
import AvatarDisplay from '../shared/AvatarDisplay';
import AvatarSelector from '../shared/AvatarSelector';
import { RichTextEditor } from '../shared/RichTextEditor';

export const TeacherSettings: React.FC = () => {
  const { user: authUser } = useAuth();
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newSubject, setNewSubject] = useState('');
  const [avatar, setAvatar] = useState<Avatar | undefined>(undefined);
  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false);

  const [profile, setProfile] = useState<TeacherProfile>({
    phone: '',
    office_location: '',
    office_hours: '',
    bio: '',
    subjects: [],
    preferred_contact_method: '',
  });

  useEffect(() => {
    if (authUser) {
      loadProfile();
    }
  }, [authUser]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the user data using 'me' which the backend will resolve to the authenticated user's DynamoDB user_id
      const userData = await getUser('me');
      setUserId(userData.user_id);
      setAvatar(userData.avatar);

      if (userData.teacher_profile) {
        setProfile({
          phone: userData.teacher_profile.phone || '',
          office_location: userData.teacher_profile.office_location || '',
          office_hours: userData.teacher_profile.office_hours || '',
          bio: userData.teacher_profile.bio || '',
          subjects: userData.teacher_profile.subjects || [],
          preferred_contact_method: userData.teacher_profile.preferred_contact_method || '',
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!userId) {
        throw new Error('User not authenticated');
      }

      await updateTeacherProfile(userId, profile);
      setSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSubject = () => {
    if (newSubject.trim() && !profile.subjects?.includes(newSubject.trim())) {
      setProfile({
        ...profile,
        subjects: [...(profile.subjects || []), newSubject.trim()],
      });
      setNewSubject('');
    }
  };

  const handleRemoveSubject = (subject: string) => {
    setProfile({
      ...profile,
      subjects: profile.subjects?.filter((s) => s !== subject) || [],
    });
  };

  const handleChange = (field: keyof TeacherProfile, value: string) => {
    setProfile({
      ...profile,
      [field]: value,
    });
  };

  const handleAvatarSelect = async (newAvatar: Avatar) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!userId) {
        throw new Error('User not authenticated');
      }

      await updateUser(userId, { avatar: newAvatar });
      setAvatar(newAvatar);
      setSuccess('Avatar updated successfully!');

      // Dispatch event to refresh avatar in navigation
      window.dispatchEvent(new Event('avatarUpdated'));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update avatar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Teacher Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Update your profile information that will be visible to your students
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Avatar Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Profile Avatar
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={3}>
              <AvatarDisplay
                avatar={avatar}
                name={authUser?.attributes?.name || 'Teacher'}
                role="teacher"
                size="large"
                showTooltip={false}
              />
              <Box>
                <Typography variant="body1" gutterBottom>
                  Customize your avatar to make your profile more fun and recognizable!
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setAvatarSelectorOpen(true)}
                >
                  Change Avatar
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={profile.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
              helperText="Optional - Share if you want students to contact you by phone"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Preferred Contact Method"
              value={profile.preferred_contact_method || ''}
              onChange={(e) => handleChange('preferred_contact_method', e.target.value)}
              placeholder="Email, Phone, Office Hours"
              helperText="How students should best reach you"
            />
          </Grid>

          {/* Office Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Office Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Office Location"
              value={profile.office_location || ''}
              onChange={(e) => handleChange('office_location', e.target.value)}
              placeholder="Room 205, Building A"
              helperText="Where students can find you"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Office Hours"
              value={profile.office_hours || ''}
              onChange={(e) => handleChange('office_hours', e.target.value)}
              placeholder="Mon-Fri 2:00 PM - 4:00 PM"
              helperText="When students can visit"
            />
          </Grid>

          {/* Subjects */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Subjects I Teach
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                fullWidth
                label="Add Subject"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSubject();
                  }
                }}
                placeholder="e.g., Mathematics, Science, History"
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubject}
                disabled={!newSubject.trim()}
              >
                Add
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {profile.subjects && profile.subjects.length > 0 ? (
                profile.subjects.map((subject) => (
                  <Chip
                    key={subject}
                    label={subject}
                    onDelete={() => handleRemoveSubject(subject)}
                    color="primary"
                    deleteIcon={<DeleteIcon />}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No subjects added yet
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Bio */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              About Me
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tell your students about yourself, your teaching philosophy, interests, etc. Use the
              rich text editor to format your bio with bold, italic, lists, and more!
            </Typography>
            <RichTextEditor
              content={profile.bio || ''}
              onChange={(html) => handleChange('bio', html)}
              placeholder="Tell your students about yourself, your teaching philosophy, interests, etc."
              minHeight={250}
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Button variant="outlined" onClick={loadProfile} disabled={saving}>
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Avatar Selector Dialog */}
      <AvatarSelector
        open={avatarSelectorOpen}
        onClose={() => setAvatarSelectorOpen(false)}
        onSelect={handleAvatarSelect}
        currentAvatar={avatar}
        role="teacher"
      />
    </Box>
  );
};

export default TeacherSettings;
